import { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceService } from '../../services/attendance';

const deriveClockStatus = (activeShift) => {
  if (!activeShift) return 'not_clocked_in';
  if (activeShift.has_active_break) return 'on_break';
  return 'clocked_in';
};

export const useAttendance = (userId) => {
  const [assignedVenues, setAssignedVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [activeShift, setActiveShift] = useState(null);
  const [recentShifts, setRecentShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError('');

    try {
      const [venues, shift, history] = await Promise.all([
        attendanceService.getAssignedVenuesForCurrentUser(userId),
        attendanceService.getMyActiveShift(userId),
        attendanceService.getMyRecentShifts(userId),
      ]);

      setAssignedVenues(venues);
      setActiveShift(shift);
      setRecentShifts(history);

      if (shift?.venue_id) {
        setSelectedVenueId(shift.venue_id);
      } else if (venues.length === 1) {
        setSelectedVenueId(venues[0].id);
      } else if (!selectedVenueId && venues.length > 1) {
        setSelectedVenueId(venues[0].id);
      }
    } catch (err) {
      setError(attendanceService.mapErrorMessage(err, 'Failed to load attendance state'));
    } finally {
      setLoading(false);
    }
  }, [selectedVenueId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const clockIn = useCallback(async () => {
    if (!selectedVenueId) {
      throw new Error('Select a venue before clocking in.');
    }

    setActionLoading(true);
    try {
      const result = await attendanceService.clockIn(selectedVenueId);
      await refresh();
      return result;
    } finally {
      setActionLoading(false);
    }
  }, [refresh, selectedVenueId]);

  const startBreak = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = await attendanceService.startBreak();
      await refresh();
      return result;
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  const endBreak = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = await attendanceService.endBreak();
      await refresh();
      return result;
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  const clockOut = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = await attendanceService.clockOut();
      await refresh();
      return result;
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  const clockStatus = useMemo(() => deriveClockStatus(activeShift), [activeShift]);
  const activeVenueLocked = Boolean(activeShift?.venue_id);

  return {
    assignedVenues,
    selectedVenueId,
    setSelectedVenueId,
    activeShift,
    recentShifts,
    loading,
    actionLoading,
    error,
    refresh,
    clockStatus,
    activeVenueLocked,
    clockIn,
    startBreak,
    endBreak,
    clockOut,
  };
};
